// SUB-OFFICE 7 — the print pass. One half-float RT (now with a sampleable depth
// texture) + one fullscreen triangle. In order: SSAO (crevice/contact shading
// reconstructed from depth), stylized grade, luminance-weighted grain (4-6%),
// 8x8 Bayer dither to ~64 levels, vignette 0.35, black-point lift clamped to
// CARBON so darkness reads as ink, never void. TIER A ONLY — tiers B/C render
// direct and keep the palette. NO bloom exists; additive glow cards under
// emissives carry the look.
//
// GRAPHICS R3 (#75) — SSAO. The scene renders tonemapped-linear into the RT;
// the triangle reconstructs view-space position from the depth texture, builds
// a per-pixel normal from screen derivatives (no separate normal buffer), and
// samples a rotated hemisphere kernel to darken contacts and crevices. Applied
// to the LINEAR colour before lin->srgb (occlusion attenuates radiance), guarded
// off bright emissives so hot tubes never grow dark halos. GLSL3 for clean
// texture()/derivatives; the material carries no three built-ins, so the version
// bump only touches this file's own two shaders.

import {
  BufferAttribute, BufferGeometry, Camera, DepthTexture, GLSL3, HalfFloatType,
  LinearFilter, Matrix4, Mesh, NearestFilter, OrthographicCamera, RawShaderMaterial,
  Scene, UnsignedIntType, Vector2, WebGLRenderTarget, WebGLRenderer,
} from 'three';

const VERT = `
in vec3 position;
out vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const FRAG = `
precision highp float;
precision highp sampler2D;
uniform sampler2D tDiffuse;
uniform sampler2D tDepth;
uniform mat4 uProj;        // camera projection (view -> clip)
uniform mat4 uProjInv;     // clip -> view
uniform vec2 uResolution;
uniform float uTime;
uniform float uGrain;      // 0.04 - 0.06
uniform float uVignette;   // 0.35
uniform float uAdj;        // adjudication state 0..1 (vignette 0.5, grain +50%, 1px split)
uniform float uProbation;  // probation desaturation toward GHOST GREY
uniform float uPop;        // single-frame 3% exposure pop on slams
uniform float uWarm;       // Appeals tungsten bias (0..1); FP-position gated
uniform float uAo;         // SSAO strength (0 = off, ~1 = full)
uniform float uAoRadius;   // hemisphere radius, view-space metres (~0.55)
in vec2 vUv;
out vec4 fragColor;

const vec3 CARBON = vec3(0.110, 0.102, 0.090);
const vec3 GHOST  = vec3(0.718, 0.702, 0.659);

// 12-tap hemisphere kernel (z>0), lengths ramped so near-contact taps dominate.
const int AO_TAPS = 12;
const vec3 KERNEL[12] = vec3[12](
  vec3( 0.5381, 0.1856, 0.4319), vec3( 0.1379, 0.2486, 0.4430),
  vec3( 0.3371, 0.5679, 0.0057), vec3(-0.6999,-0.0451, 0.0019),
  vec3( 0.0689,-0.1598, 0.8547), vec3( 0.0560, 0.0069, 0.1843),
  vec3(-0.0146, 0.1402, 0.0762), vec3( 0.0100,-0.1924, 0.0344),
  vec3(-0.3577,-0.5301, 0.4358), vec3(-0.3169, 0.1063, 0.0158),
  vec3( 0.0103,-0.5869, 0.0046), vec3(-0.0897,-0.4940, 0.3287)
);

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}
float Bayer2(vec2 a) { a = floor(a); return fract(a.x / 2.0 + a.y * a.y * 0.75); }
float Bayer4(vec2 a) { return Bayer2(0.5 * a) * 0.25 + Bayer2(a); }
float Bayer8(vec2 a) { return Bayer4(0.5 * a) * 0.25 + Bayer2(a); }

vec3 lin2srgb(vec3 c) {
  return pow(max(c, vec3(0.0)), vec3(1.0 / 2.2));
}

// Reconstruct view-space position from the depth texture at a UV.
vec3 viewPosAt(vec2 uv) {
  float d = texture(tDepth, uv).r;
  vec4 clip = vec4(uv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
  vec4 v = uProjInv * clip;
  return v.xyz / v.w;
}

// Depth-reconstructed SSAO. Returns an occlusion factor in [0,1] (1 = open).
float computeAO(vec2 uv, out float isSky) {
  float d = texture(tDepth, uv).r;
  isSky = step(0.99999, d);
  if (uAo <= 0.0 || isSky > 0.5) return 1.0;

  vec3 P = viewPosAt(uv);
  // normal from screen-space derivatives of the reconstructed position
  vec3 N = normalize(cross(dFdx(P), dFdy(P)));

  // per-pixel rotation to trade banding for noise (the grain pass hides it)
  float rot = hash(uv * uResolution) * 6.2831853;
  vec3 rvec = normalize(vec3(cos(rot), sin(rot), 0.0));
  vec3 T = normalize(rvec - N * dot(rvec, N));
  vec3 B = cross(N, T);
  mat3 TBN = mat3(T, B, N);

  float bias = 0.03; // raised to suppress false self-occlusion on flat spans
  float occ = 0.0;
  for (int i = 0; i < AO_TAPS; i++) {
    vec3 samplePos = P + (TBN * KERNEL[i]) * uAoRadius;   // view space
    vec4 off = uProj * vec4(samplePos, 1.0);
    off.xyz /= off.w;
    vec2 sUv = off.xy * 0.5 + 0.5;
    if (sUv.x < 0.0 || sUv.x > 1.0 || sUv.y < 0.0 || sUv.y > 1.0) continue;
    float sceneZ = viewPosAt(sUv).z;                       // actual geometry z there
    float rangeCheck = smoothstep(0.0, 1.0, uAoRadius / max(1e-4, abs(P.z - sceneZ)));
    occ += (sceneZ >= samplePos.z + bias ? 1.0 : 0.0) * rangeCheck;
  }
  float ao = 1.0 - (occ / float(AO_TAPS));
  return ao;
}

void main() {
  vec2 px = 1.0 / uResolution;
  float split = uAdj * 1.0;
  vec3 col;
  col.r = texture(tDiffuse, vUv + vec2(px.x * split, 0.0)).r;
  col.g = texture(tDiffuse, vUv).g;
  col.b = texture(tDiffuse, vUv - vec2(px.x * split, 0.0)).b;

  // SSAO — multiply the linear radiance by occlusion, but spare bright emissives
  // (hot tubes / the Machine voice) so they never gain a dark contact halo.
  float isSky;
  float ao = computeAO(vUv, isSky);
  float lumaLin = dot(col, vec3(0.299, 0.587, 0.114));
  float emissiveGuard = smoothstep(0.55, 0.95, lumaLin); // 1 where already bright
  float aoApplied = mix(mix(1.0, ao, uAo), 1.0, emissiveGuard);
  col *= aoApplied;

  col = lin2srgb(col);

  // GRAPHICS R2 (#75) — STYLIZED GRADE. The tonemapped Bureau lands its midtones
  // in one narrow beige band, which reads as washed. A mild saturation lift plus
  // a local-contrast S around mid separates the greys and lets the authored hues
  // (amber marks, brass, ghost-grey figures) actually read, without touching the
  // palette hexes. Kept gentle (1.13/1.12) to stay clean-stylized, never graded-
  // film. Tier A only, like the rest of this pass; B/C keep the flat palette.
  {
    float _gl = dot(col, vec3(0.299, 0.587, 0.114));
    col = mix(vec3(_gl), col, 1.13);        // saturation
    col = (col - 0.5) * 1.12 + 0.5;         // contrast S, mid pivot
    col = clamp(col, 0.0, 1.0);
  }

  // probation: the world drains toward GHOST GREY
  float luma = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(col, mix(vec3(luma), GHOST, 0.35), uProbation * 0.8);

  // Appeals tungsten: a 3% red / 1.5% green lift, 2% blue cut — the wood room
  // feels lamp-lit without a third light. Gated to the FP Appeals footprint.
  col.r *= 1.0 + uWarm * 0.03;
  col.g *= 1.0 + uWarm * 0.015;
  col.b *= 1.0 - uWarm * 0.02;

  // luminance-weighted grain
  float g = hash(vUv * uResolution + fract(uTime) * 61.7) - 0.5;
  float amt = uGrain * (1.0 + 0.5 * uAdj);
  col += g * amt * (0.35 + 0.65 * luma);

  // 8x8 Bayer dither to ~64 levels
  float dth = Bayer8(gl_FragCoord.xy) - 0.5;
  col = floor(col * 64.0 + dth + 0.5) / 64.0;

  // vignette
  vec2 q = vUv - 0.5;
  float vig = 1.0 - dot(q, q) * (uVignette + uAdj * 0.3) * 2.4;
  col *= clamp(vig, 0.0, 1.0);

  // slam pop
  col *= 1.0 + uPop * 0.03;

  // black-point lift: darkness is ink
  col = CARBON + col * (1.0 - CARBON);

  fragColor = vec4(col, 1.0);
}
`;

export class PostPipeline {
  enabled = true;
  private rt: WebGLRenderTarget;
  private quadScene = new Scene();
  private quadCam = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private mat: RawShaderMaterial;
  private res = new Vector2(1, 1);
  private pop = 0;
  private warm = 0;
  private warmTarget = 0;

  constructor() {
    const depth = new DepthTexture(2, 2, UnsignedIntType);
    depth.minFilter = NearestFilter;
    depth.magFilter = NearestFilter;
    this.rt = new WebGLRenderTarget(2, 2, {
      type: HalfFloatType,
      minFilter: LinearFilter,
      magFilter: LinearFilter,
      depthBuffer: true,
      depthTexture: depth,
      samples: 0,
    });
    this.mat = new RawShaderMaterial({
      glslVersion: GLSL3,
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        tDiffuse: { value: this.rt.texture },
        tDepth: { value: this.rt.depthTexture },
        uProj: { value: new Matrix4() },
        uProjInv: { value: new Matrix4() },
        uResolution: { value: this.res },
        uTime: { value: 0 },
        uGrain: { value: 0.05 },
        uVignette: { value: 0.35 },
        uAdj: { value: 0 },
        uProbation: { value: 0 },
        uPop: { value: 0 },
        uWarm: { value: 0 },
        uAo: { value: 0.9 },
        uAoRadius: { value: 0.55 },
      },
      depthTest: false,
      depthWrite: false,
    });
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    const tri = new Mesh(geo, this.mat);
    tri.frustumCulled = false;
    this.quadScene.add(tri);
  }

  setSize(w: number, h: number, dpr: number): void {
    const rw = Math.max(2, Math.floor(w * dpr));
    const rh = Math.max(2, Math.floor(h * dpr));
    this.rt.setSize(rw, rh);
    this.res.set(rw, rh);
  }

  /** Ceremony state hooks (wired by the ceremony slice). */
  setAdjudication(v: number): void {
    this.mat.uniforms.uAdj.value = v;
  }
  setProbation(v: number): void {
    this.mat.uniforms.uProbation.value = v;
  }
  /** Single-frame exposure pop; decays automatically. */
  slamPop(): void {
    this.pop = 1;
  }

  /** SSAO strength (0 disables). Live-tunable via the __DEV__ handle for A/B. */
  setAo(v: number): void {
    this.mat.uniforms.uAo.value = v;
  }

  /** Appeals tungsten bias target (0 or 1); eased in render so entry/exit of
   *  the wood room breathes rather than snaps. Driven by an FP-position test. */
  setWarm(on: number): void {
    this.warmTarget = on;
  }

  render(renderer: WebGLRenderer, scene: Scene, camera: Camera, dt: number): void {
    // renderer.info must survive both passes so the debug hotkey tells the truth
    if (renderer.info.autoReset) renderer.info.autoReset = false;
    renderer.info.reset();
    if (!this.enabled) {
      renderer.render(scene, camera);
      return;
    }
    this.mat.uniforms.uTime.value += dt;
    this.mat.uniforms.uPop.value = this.pop;
    this.pop = Math.max(0, this.pop - dt * 12);
    // ease the tungsten bias (~0.6s in/out) toward its gated target
    this.warm += (this.warmTarget - this.warm) * Math.min(1, dt * 4);
    this.mat.uniforms.uWarm.value = this.warm;
    // feed the camera matrices so SSAO can reconstruct view-space position
    (this.mat.uniforms.uProj.value as Matrix4).copy(camera.projectionMatrix);
    (this.mat.uniforms.uProjInv.value as Matrix4).copy(camera.projectionMatrixInverse);
    renderer.setRenderTarget(this.rt);
    renderer.render(scene, camera);
    renderer.setRenderTarget(null);
    renderer.render(this.quadScene, this.quadCam);
  }

  dispose(): void {
    this.rt.dispose();
  }
}
